import { useState } from "react";

function SitetestWeb() {
  const SITE_ID = "siheung02";
  const CASE_ID = 2;
  const SENSOR_URL = "https://dtxiot.net/dtx-iot-v3/api/sensors?sensorXml=";
  const SIHEUNG_WEB_RELAY_URL =
    process.env.REACT_APP_SIHEUNG_WEB_RELAY_URL || "http://127.0.0.1:8789";
  const NORMAL_RESISTANCE_MIN = 1997;
  const NORMAL_RESISTANCE_MAX = 2023;
  const ERROR_RESISTANCE_MIN = 200;
  const ERROR_RESISTANCE_MAX = 400;

  const [recentClick, setRecentClick] = useState("클릭 없음");
  const [serverStatus, setServerStatus] = useState("대기");
  const [relayStatus, setRelayStatus] = useState("대기");
  const [isSending, setIsSending] = useState(false);

  const buttonConfigs = [
    {
      label: "ALL OK",
      description: "1,2,3채널 모두 정상",
      errorChannels: [],
      buttonClassName: "bg-emerald-600 text-white",
    },
    {
      label: "ALL ERROR",
      description: "1,2,3채널 모두 에러",
      errorChannels: [1, 2, 3],
    },
    {
      label: "1ch error",
      description: "1채널만 에러",
      errorChannels: [1],
    },
    {
      label: "2ch error",
      description: "2채널만 에러",
      errorChannels: [2],
    },
    {
      label: "3ch error",
      description: "3채널만 에러",
      errorChannels: [3],
    },
  ];

  const getRandomBattery = () =>
    (Math.random() * (15.19 - 12.67) + 12.67).toFixed(2);

  const getNormalResistance = () =>
    Math.round(
      Math.random() * (NORMAL_RESISTANCE_MAX - NORMAL_RESISTANCE_MIN) +
        NORMAL_RESISTANCE_MIN,
    );

  const getErrorResistance = () =>
    Math.round(
      Math.random() * (ERROR_RESISTANCE_MAX - ERROR_RESISTANCE_MIN) +
        ERROR_RESISTANCE_MIN,
    );

  const createWebSensorXml = ({ errorChannels }) => {
    const channelXml = [1, 2, 3]
      .map(
        (channel) => {
          const isErrorChannel = errorChannels.includes(channel);

          return `
      <ch>
        <chNum>${channel}</chNum>
        <status>${isErrorChannel ? 0 : 1}</status>
        <resistance>${
          isErrorChannel ? getErrorResistance() : getNormalResistance()
        }</resistance>
      </ch>`;
        },
      )
      .join("");

    return `<xml>
  <siteId>${SITE_ID}</siteId>
  <case>
    <caseId>${CASE_ID}</caseId>
    <battery>${getRandomBattery()}</battery>
    <bp>${channelXml}
    </bp>
  </case>
</xml>`;
  };

  const sendWebSensorMessage = async (xmlData) => {
    const fullUrl = `${SENSOR_URL}${encodeURIComponent(xmlData)}`;
    await fetch(fullUrl, {
      method: "POST",
      mode: "no-cors",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: fullUrl,
    });
  };

  const sendWebRelayCommand = async ({ button, xmlText, clickedAt }) => {
    const response = await fetch(
      `${SIHEUNG_WEB_RELAY_URL}/api/siheung-web/command`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          button,
          xmlText,
          clickedAt,
        }),
      },
    );

    let payload = null;
    try {
      payload = await response.json();
    } catch (error) {
      payload = null;
    }

    if (!response.ok || !payload?.ok) {
      throw new Error(payload?.message || "자동전송 등록 실패");
    }

    return payload;
  };

  const formatRelayDateTime = (value) => {
    if (!value) {
      return "-";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return parsedDate.toLocaleString("ko-KR", {
      timeZone: "Asia/Seoul",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  };

  const getStatusClassName = (statusText) => {
    if (statusText.includes("실패") || statusText.includes("미등록")) {
      return "text-red-500";
    }

    if (statusText.includes("완료") || statusText.includes("등록")) {
      return "text-emerald-600";
    }

    if (statusText.includes("전송 중")) {
      return "text-slate-600";
    }

    return "text-slate-600";
  };

  const handleButtonClick = async (button) => {
    const xmlData = createWebSensorXml({
      errorChannels: button.errorChannels,
    });
    const clickedAt = new Date().toISOString();

    setIsSending(true);
    setServerStatus("전송 중...");
    setRelayStatus("등록 대기...");

    try {
      await sendWebSensorMessage(xmlData);
      setRecentClick(button.label);
      setServerStatus("전송 요청 완료");
    } catch (error) {
      console.error(error);
      setServerStatus(error.message || "브라우저 전송 실패");
      setRelayStatus("미등록 (실서버 요청 실패)");
      setIsSending(false);
      return;
    }

    try {
      const relayResponse = await sendWebRelayCommand({
        button: button.label,
        xmlText: xmlData,
        clickedAt,
      });
      setRelayStatus(
        `등록 완료 (다음 전송 ${formatRelayDateTime(relayResponse.nextRunAt)})`,
      );
    } catch (error) {
      console.error(error);
      setRelayStatus(error.message || "자동전송 등록 실패");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="flex justify-center items-center px-2">
      <div className="flex-col justify-center items-center">
        <div className="my-[50px] lg:px-[150px] flex-col">
          <div className="text-3xl font-bold">시흥현장 WEB</div>
          <div className="text-base text-slate-500 pt-3">
            신규 WEB 컨트롤러 3채널 테스트
          </div>

          <div className="space-y-3 pt-6">
            {buttonConfigs.map((button) => (
              <div key={button.label} className="flex items-center gap-4">
                <button
                  className={`${
                    button.buttonClassName || "bg-slate-600 text-white"
                  } px-4 lg:px-10 py-3 min-w-[140px] ${
                    isSending ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  disabled={isSending}
                  onClick={() => handleButtonClick(button)}
                >
                  {button.label}
                </button>
                <div className="text-base lg:text-lg">{button.description}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 font-semibold text-red-500">
            최근클릭 : {recentClick}
          </div>
          <div
            className={`mt-2 font-medium ${getStatusClassName(serverStatus)}`}
          >
            서버 전송 : {serverStatus}
          </div>
          <div
            className={`mt-2 font-medium ${getStatusClassName(relayStatus)}`}
          >
            자동전송 : {relayStatus}
          </div>
        </div>
      </div>
    </div>
  );
}

export default SitetestWeb;
